import humps from 'lodash-humps';
import { addDays, isBefore, format, endOfMonth, parse, subDays, addMinutes } from 'date-fns';
import { formatToTimeZone } from 'date-fns-timezone';
import { assign, compact, filter, forEach, includes, map, reduce, union } from 'lodash';

const state = {
    availabilities: [],
    closestAvailabilities: [],
    daysComplete: [],
    upcomingAvailabilities: [],
    pastAvailabilities: [],
    dailyAvailabilities: [],
};

const getters = {
    getAvailabilities: (state) => state.availabilities,
    getClosestAvailabilities: (state) => state.closestAvailabilities,
    getDaysComplete: (state) => state.daysComplete,
    getUpcomingAvailabilities: (state) => state.upcomingAvailabilities,
    getPastAvailabilities: (state) => state.pastAvailabilities,
    getDailyAvailabilities: (state) => state.dailyAvailabilities,

    /**
     * Build a sorted array of timeslot from an array of availabilities
     * Warning, this getter return a function, it should be wrap into a computed function to get the caching performance
     */
    extractTimeSlots: (state) => (avails) => {
        return reduce(avails, (result, avail) => {
            // Force appts to be displayed on 5 minutes intervals
            forEach(['start', 'stop'], prop => {
                let timeValue = avail[prop];
                const remainderToFive = +format(timeValue, 'mm') % 5;
                if (remainderToFive !== 0) {
                    // avail.start : add mins to be on 5 minutes interval || avail.stop: remove mins to be on 5 minutes interval
                    const forceDiff = prop === 'start' ? 5 - remainderToFive : -remainderToFive;
                    avail = {
                        ...avail,
                        [prop]: addMinutes(avail[prop], forceDiff).getTime(),
                    };
                }
            });
            const length = avail.length * 60 * 1000;
            let time = avail.start;
            const stop = avail.stop;

            // loop only if availability duration is greater or equal than length
            if ((stop - time) >= length) {
                do {
                    let date = format(time, 'YYYY-MM-DD HH:mm:ss');

                    let formattedTime = format(time, 'HH:mm');
                    if (!includes(state.pastAvailabilities, date)) {
                        const obj = assign({}, {
                            time: formattedTime,
                            timeLabel: formattedTime + (avail.resourceCount > 1 ? ' (' + avail.resourceCount + ')' : ''),
                            timeMS: time,
                        }, { avail });
                        result.push(obj);
                    }
                    time += length;
                } while (stop - length >= time);
            }
            return result.sort((a, b) => a.timeMS - b.timeMS);
        }, []);
    },

    isDateCompleteEvent: (state, getters) => date => {
        return includes(getters.getDaysComplete, date);
    },

    isDatePastEvent: (state, getters) => date => {
        return includes(getters.getPastAvailabilities, date);
    },

    isDateUpcomingEvent: (state, getters) => date => {
        return includes(getters.getUpcomingAvailabilities, date);
    },
};

const mutations = {
    UPDATE_AVAILABILITIES(state, payload) {
        state.availabilities = payload.availabilities;
        state.upcomingAvailabilities = payload.upcoming;
        state.pastAvailabilities = payload.pastAvailabilities;
    },
    UPDATE_AVAILABILITIES_ONLY(state, payload) {
        state.availabilities = payload.availabilities;
    },
    UPDATE_DAYS_COMPLETE(state, payload) {
        state.daysComplete = payload.daysComplete;
    },
    UPDATE_UPCOMING_AVAILABILITIES(state, payload) {
        state.upcomingAvailabilities = payload.upcomingAvailabilities;
    },
    UPDATE_PAST_AVAILABILITIES(state, payload) {
        state.pastAvailabilities = payload.pastAvailabilities;
    },
    UPDATE_DAILY_AVAILABILITIES(state, payload) {
        state.dailyAvailabilities = payload;
    },
    UPDATE_CLOSEST_AVAILABILITIES(state, payload) {
        state.closestAvailabilities = payload;
    },
};

const actions = {
    /**
     * @description Fetch Availabilities from the api
     * @param commit
     * @param rootGetters
     * @param dispatch
     * @param dateStart
     * @param dateStop
     * @param serviceId
     * @param placeId
     * @param light
     * @param filters
     * @param clientTimeZone
     * @returns {Promise<*>}
     */
    async getAvailabilities({ commit, rootGetters, dispatch }, [dateStart, dateStop, serviceId, placeId, filters = null, clientTimeZone = null]) {
        const establishmentId = rootGetters['establishment/getEstablishmentId'];
        dateStart = dateStart || format(new Date(), 'YYYY-MM-DD');
        dateStop = dateStop || format(endOfMonth(new Date()), 'YYYY-MM-DD');
        const gapMode = rootGetters['gap/isGapModeOn'];
        const gapHeadOfficeId = rootGetters['gap/getGapHeadOfficeId'];

        const response = await this._vm.$api.schedules.getPublicSchedule(establishmentId, dateStart, dateStop, serviceId, placeId, filters, clientTimeZone, gapMode, gapHeadOfficeId);
        const data = humps(response.data);
        commit('UPDATE_AVAILABILITIES', data);
        commit('UPDATE_DAYS_COMPLETE', data);
        commit('UPDATE_UPCOMING_AVAILABILITIES', data);
        commit('UPDATE_PAST_AVAILABILITIES', data);
    },

    /**
     * Fetch Closest Avails From the API by passing him a target Datetime
     */
    async fetchClosestAvails({ state, commit, rootGetters, dispatch }, [dateTarget, dateStart, dateStop, serviceId, placeId, filters = null, clientTimeZone = null, limit = null, minResourceCount = null]) {
        const establishmentId = rootGetters['establishment/getEstablishmentId'];
        const service = rootGetters['services/getServiceByID'][serviceId][0];
        const gapMode = rootGetters['gap/isGapModeOn'];
        const gapHeadOfficeId = rootGetters['gap/getGapHeadOfficeId'];

        const response = await this._vm.$api.schedules.getClosestSchedule(establishmentId, dateTarget, dateStart, dateStop, serviceId, placeId, filters, clientTimeZone, limit, minResourceCount, gapMode, gapHeadOfficeId);
        const data = response.data;
        const avails = await dispatch('formatAvail', { data, clientTimeZone, service });
        commit('UPDATE_CLOSEST_AVAILABILITIES', avails);
        commit('UPDATE_DAILY_AVAILABILITIES', union(getters.getDailyAvailabilities(state), avails));
    },

    async getCompleteAvailabilitiesBetweenPeriod({ commit, rootGetters, dispatch }, [dateStart, dateStop, serviceId, placeId, filters = null, clientTimeZone = null]) {
        const establishmentId = rootGetters['establishment/getEstablishmentId'];
        const service = rootGetters['services/getServiceByID'][serviceId][0];
        dateStart = dateStart || format(new Date(), 'YYYY-MM-DD');
        dateStop = dateStop || format(endOfMonth(new Date()), 'YYYY-MM-DD');
        const gapMode = rootGetters['gap/isGapModeOn'];
        const gapHeadOfficeId = rootGetters['gap/getGapHeadOfficeId'];

        try {
            const response = await this._vm.$api.schedules.getCompletePublicPeriodSchedule(establishmentId, dateStart, dateStop, serviceId, placeId, filters, clientTimeZone, gapMode, gapHeadOfficeId);
            const data = humps(response.data);
            const avails = await dispatch('formatAvail', { data, clientTimeZone, service });
            commit('UPDATE_DAILY_AVAILABILITIES', compact(avails));
        }
        catch(error) {
            commit('UPDATE_DAILY_AVAILABILITIES', []);
            commit('appointments/REMOVE_NEXT_APPOINTMENT_INFO', null, { root: true });
            throw error;
        }
    },

    /**
     * @description Find The first availability for a place(s), a service, [filters], [timezone]
     * @param rootGetters
     * @param places
     * @param service
     * @param filters
     * @param clientTimeZone
     * @returns {Q.Promise<void>}
     */
    async getFirstAvailability({ rootGetters }, [places, service, filters, clientTimeZone = null, dateStart = '', dateStop = '']) {
        const establishmentId = rootGetters['establishment/getEstablishmentId'];
        const gapMode = rootGetters['gap/isGapModeOn'];
        const gapHeadOfficeId = rootGetters['gap/getGapHeadOfficeId'];

        const response = await this._vm.$api.schedules.getFirstAvailabilityNextYear(establishmentId, places.join(','), service, filters, clientTimeZone, dateStart, dateStop, gapMode, gapHeadOfficeId);
        return humps(response.data);
    },

    /**
     * Get a specific PREST for one place, for one resource, for one EST
     * @param commit
     * @param rootGetters
     * @param resourceId
     * @param placeId
     * @param serviceId
     * @returns {Q.Promise<any>}
     */
    async getOnePrest({ commit, rootGetters }, { resourceId, placeId, serviceId }) {
        const establishmentId = rootGetters['establishment/getEstablishmentId'];
        const response = await this._vm.$api.resources.getOnePrest(establishmentId, placeId, resourceId, serviceId);
        return humps(response.data);
    },

    /**
     * @description Format Availabilities receive from the API for frontend compatibilities
     * @param commit
     * @param data
     * @param clientTimeZone
     * @param allPrest
     * @param service
     * @returns {Array} => formated Avails
     */
    formatAvail({ commit }, { data, clientTimeZone, service }) {
        const result = map(data.availabilities, avail => {
            if (avail.status) {
                let date, dateEnd, start, stop;

                if (clientTimeZone) {
                    const formatDate = 'YYYY-MM-DD HH:mm:ss';
                    start = formatToTimeZone(avail.start, formatDate, { timeZone: clientTimeZone });
                    stop = formatToTimeZone(avail.stop, formatDate, { timeZone: clientTimeZone });
                    date = format(start, 'YYYY-MM-DD');
                    dateEnd = format(stop, 'YYYY-MM-DD');
                    start = parse(start).getTime();
                    stop = parse(stop).getTime();
                }
                else {
                    date = avail.start.substr(0, 10);
                    dateEnd = avail.start.substr(0, 10);
                    start = parse(avail.start).getTime();
                    stop = parse(avail.stop).getTime();
                }

                return assign(avail, { date }, { dateEnd }, { start }, { stop }, { service });
            }
        });
        return result;
    },

    /**
     * @description Remove all dates outside the tolerance date range for Kids & Pregancy
     * @param state
     * @param commit
     * @param dateMin
     * @param dateMax
     */
    purgeAvailsPastDayUpcomingDaysAndDaysComplete({ state, commit }, { dateMin, dateMax }) {
        const stateToPurge = {
            'UPDATE_AVAILABILITIES_ONLY': 'availabilities',
            'UPDATE_DAYS_COMPLETE': 'daysComplete',
            'UPDATE_UPCOMING_AVAILABILITIES': 'upcomingAvailabilities',
            'UPDATE_PAST_AVAILABILITIES': 'pastAvailabilities',
        };

        dateMin = subDays(dateMin, 1);
        dateMax = addDays(dateMax, 1);

        forEach(stateToPurge, (prop, mutation) => {
            let acc = {};
            acc[prop] = filter(state[prop], (date) => {
                return isBefore(dateMin, date) && isBefore(date, dateMax);
            });
            commit(mutation, acc);
        });
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
